// An answer's rows found by a key, built once per answer (#1309). A site's two
// device lists look up each row's convergence, readiness, clients, plan and
// work-list entry by serial, and a `.find` per row over an answer of N rows is
// N² comparisons per render — and the page renders again on every switch
// reader's answer. This builds the index the first time an answer is asked and
// answers every later lookup on that answer from it.
//
// **Keyed on the answer's identity, and kept no longer than the answer.** The
// index lives in a `WeakMap` keyed by the array the read returned, so a new
// answer is a new index and an answer nobody holds takes its index with it.
// That is a `useMemo` keyed on the answer, with no hook: the pure functions
// that read it keep their signatures and stay callable from anywhere, and
// nothing is cached across answers or pages (ADR-0419 `1d`). An answer is never
// mutated after it is read, which is what makes its identity a sound key.
//
// **The first row for a key wins**, which is what `.find` answered: a lookup
// here says exactly what the scan it replaces said.

const built = new WeakMap<readonly unknown[], Map<PropertyKey, Map<unknown, unknown>>>();

/** The first row of `rows` for each value of `key`. */
export function firstBy<T, K extends keyof T>(rows: readonly T[], key: K): ReadonlyMap<T[K], T> {
  let byKey = built.get(rows);
  if (!byKey) { byKey = new Map(); built.set(rows, byKey); }
  let index = byKey.get(key) as Map<T[K], T> | undefined;
  if (!index) {
    index = new Map();
    for (const r of rows) if (!index.has(r[key])) index.set(r[key], r);
    byKey.set(key, index as Map<unknown, unknown>);
  }
  return index;
}
